import React, { useState } from 'react'
import { useRouter } from 'next/router'

import {
  useGetMaterialByModule,
  useGetModulePreview,
} from 'api/microlearning/useAPIModule'
import { useModuleExpanderStore } from 'stores/microlearning'
import { GATrackEvent } from 'utils/googleAnalytics'
import ModuleDetailsDesktop from './ModuleDetails.desktop'
import ModuleDetailsMobile from './ModuleDetails.mobile'

const ModuleDetails = ({ isDesktop = false }) => {
  const { query, back } = useRouter()
  const [dialogType, setDialogType] = useState('module-detail')
  const modulePreview = useGetModulePreview(query.modul as string)
  const {
    isLoading: materialDataIsLoading,
    isSuccess: materialDataIsSuccess,
    data: materialData,
    refetch: refetchMaterialData,
  } = useGetMaterialByModule(query.modul as string)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const { lastExpandedIds, setLastExpandedIds } = useModuleExpanderStore()

  const isLoading = modulePreview.isLoading || materialDataIsLoading
  const isSuccess = modulePreview.isSuccess && materialDataIsSuccess
  const isInitialSet = React.useRef(false)

  React.useEffect(() => {
    if (!!query.materi && !isInitialSet.current) {
      const referredMateri = `${query.topik}_${query.modul}_${query.materi}`
      if (!lastExpandedIds.includes(referredMateri)) {
        setLastExpandedIds([...lastExpandedIds, referredMateri])
      }
      isInitialSet.current = true
    }

    if (query.videoId) {
      setDialogType('video')
      setIsDrawerOpen(true)
    }

    if (query.postTest) {
      setDialogType('post-test')
      setIsDrawerOpen(true)
    }

    if (!query.videoId && !query.postTest) {
      setIsDrawerOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastExpandedIds, setLastExpandedIds, query])

  const toggleShowDetail = async () => {
    setDialogType('module-detail')
    await setIsDrawerOpen((isShown) => !isShown)
    if (isDrawerOpen) {
      GATrackEvent({
        eventName: 'learning_activity',
        eventParams: {
          event_trigger: 'click_view_module_detail',
          action_type: 'view_module_detail',
          meta: JSON.stringify({
            module_id: query.modul,
          }),
        },
        sendDefaultParams: true,
      })
    }
  }

  const toggleMateriExpand = (currentId: string) => {
    if (lastExpandedIds.includes(currentId)) {
      return setLastExpandedIds(
        lastExpandedIds.filter((id) => id !== currentId)
      )
    }

    return setLastExpandedIds([...lastExpandedIds, currentId])
  }

  const handleDialogClose = () => {
    setIsDrawerOpen(false)
    if (dialogType === 'video') {
      refetchMaterialData()
      modulePreview.refetch()

      back()
    }
  }

  if (isDesktop) {
    return (
      <ModuleDetailsDesktop
        modulePreview={modulePreview?.data}
        materialData={materialData}
        isLoading={isLoading}
        isSuccess={isSuccess}
        dialogType={dialogType}
        toggleShowDetail={toggleShowDetail}
        toggleMateriExpand={toggleMateriExpand}
        isDrawerOpen={isDrawerOpen}
        handleDialogClose={handleDialogClose}
        lastExpandedIds={lastExpandedIds}
      />
    )
  }

  return (
    <ModuleDetailsMobile
      modulePreview={modulePreview?.data}
      isLoading={isLoading}
      isSuccess={isSuccess}
      materialData={materialData}
      toggleShowDetail={toggleShowDetail}
      toggleMateriExpand={toggleMateriExpand}
      isDrawerOpen={isDrawerOpen}
      setIsDrawerOpen={setIsDrawerOpen}
      lastExpandedIds={lastExpandedIds}
    />
  )
}

export default ModuleDetails
